<template>
	<main v-if="product" class="space-y-section">
		<Breadcrumbs :items="breadcrumbs" class="container text-body-tertiary" />
		<ProductDetail :product="product" />
	</main>
</template>

<script setup lang="ts">
	import slugify from 'slugify';
	import {storeToRefs} from 'pinia';
	import {last, flow, split} from '@dovca/toolbox';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import type {BreadcrumbItem} from '@/types/breadcrumbs';
	import {useProductCatalogStore} from '~/stores/productCatalog';
	import {GsoaProductCatalogService} from '~/openapi';
	import {GLOBAL_BRANCH_SLUG} from '#shared/constants';

	definePageMeta({title: 'Celá nabídka - produkt | Globus'});

	const productSlug = useMonadicRouteParam('product');
	const vanr = computed(() => flow(
		productSlug.value!,
		split('-'),
		last,
	));
	const categoryRouteParams = useVariadicRouteParam('category');

	const productCatalogStore = useProductCatalogStore();
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketInstance, selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);
	const {categories} = storeToRefs(productCatalogStore);

	const houseNumber = computed(() => Number(selectedHypermarketWithFallbackInstance.value?.content.gsoaId));

	const {data: product, pending} = await useAsyncData(
		`product-${vanr.value}`,
		async () => {
			if (!houseNumber.value || !vanr.value) {
				return null;
			}

			const [product] = await Promise.all([
				GsoaProductCatalogService.getHouseProductByVanr({
					houseNumber: houseNumber.value,
					productId: vanr.value!,
				}),
				productCatalogStore.fetchCategories(),
			]);

			return product;
		},
		{watch: [houseNumber]},
	);

	const canShowProductDetail = computed(() => !!selectedHypermarket.value && !pending.value);

	if (!product.value && !!houseNumber.value && canShowProductDetail.value) {
		await tryDynamicRedirectOrShowError({statusCode: 404});
	}

	const normilizeHouseProductSlug = useNormilizeHouseProductSlug();
	await normilizeHouseProductSlug(product.value);

	const branchSpecificPath = useBranchSpecificPath();

	const breadcrumbs = computed(() => {
		const partialBreadcrumbs: Array<Partial<BreadcrumbItem> & Pick<BreadcrumbItem, 'name'>> = [
			...categoryRouteParams.value.reduce<Array<Partial<BreadcrumbItem> & Pick<BreadcrumbItem, 'name'>>>(
				(acc, categorySlug) => {
					const previousItem = last(acc)!;
					const category = categories.value?.productCategoriesEshop?.find((item) => slugify(item.categoryName, {lower: true}) === categorySlug);

					return category
						? [
							...acc,
							{
								slug: `${String(previousItem.slug ?? '')}/${categorySlug}`,
								name: category.categoryName || '',
							},
						]
						: acc;
				}, [
					{
						id: 0,
						parent_id: null,
						slug: selectedHypermarket.value ?? GLOBAL_BRANCH_SLUG,
						name: selectedHypermarketInstance.value?.content.name || 'Globus',
					},
					{
						id: 1,
						parent_id: 0,
						slug: branchSpecificPath('/hypermarket/cela-nabidka').value,
						name: 'Celá nabídka',
					},
					{
						id: 2,
						parent_id: 1,
						slug: '',
						name: product.value?.name ?? '',
					},
				],
			),
		];

		return partialBreadcrumbs.map((item, index) => ({
			...item,
			id: index,
			parent_id: index - 1,
		}));
	});

	useMetaTitle(`${product.value?.name ?? ''} | Globus`);
</script>
